/**
 * Route photo management operations
 */

import { doc, collection, getDocs, writeBatch } from 'firebase/firestore'
import { db } from '../../../lib/firebase'
import { serializeForFirebase } from '../utils'

/**
 * Clean up photos with duplicate/index-based IDs and replace with proper photo IDs
 * @param routeId - The route ID to clean up
 * @returns True if cleanup was performed, false otherwise
 */
export const cleanupRoutePhotos = async (routeId: string): Promise<boolean> => {
  try {
    const routeBasePath = doc(db, 'routes', routeId)
    const photosSnapshot = await getDocs(collection(routeBasePath, 'photos'))
    
    // Check if we have any index-based photo documents
    const indexBasedDocs = photosSnapshot.docs.filter(doc => /^\d+$/.test(doc.id))
    
    if (indexBasedDocs.length === 0) {
      console.log('No index-based photos found, skipping cleanup')
      return false
    }
    
    console.log(`🧹 Found ${indexBasedDocs.length} index-based photo documents to clean up`)
    
    const batch = writeBatch(db)
    let cleanupCount = 0
    
    // Delete all index-based documents
    indexBasedDocs.forEach(doc => {
      batch.delete(doc.ref)
      cleanupCount++
    })
    
    // Re-add photos with their actual IDs
    const photoMap = new Map()
    photosSnapshot.docs.forEach(docSnap => {
      const photo = docSnap.data()
      if (photo.id && !photoMap.has(photo.id)) {
        photoMap.set(photo.id, photo)
      }
    })
    
    // Write photos back with proper IDs
    photoMap.forEach((photo, photoId) => {
      const photoRef = doc(collection(routeBasePath, 'photos'), photoId)
      batch.set(photoRef, serializeForFirebase(photo))
    })
    
    await batch.commit()
    console.log(`✅ Cleaned up ${cleanupCount} index-based photo documents`)
    
    return true
  } catch (error) {
    console.error('Failed to cleanup route photos:', error)
    return false
  }
}